// Who sees which Hub section (marketing foundation, CEO 2026-10-21).
// Mirrors apps/api/src/services/staff-access.ts — the API enforces every
// read and write; this only decides what Hub shows. The offline gate
// qa-marketing-foundation checks the lists agree.
//
//   Marketing  → admin, marketing, finance (finance reads; it doesn't run it)
//   everything else → the operations staff roles, unchanged for them; a
//   marketing-only user doesn't see those sections.

export const OPERATIONS_ROLES = ["admin", "client_rep", "order_rep", "production_rep", "finance"] as const;
export const MARKETING_VIEW_ROLES = ["admin", "marketing", "finance"] as const;
export const MARKETING_EDIT_ROLES = ["admin", "marketing"] as const;

export type HubSection = "home" | "products" | "studio" | "website" | "workflow" | "marketing" | "finance";

export const SECTION_ROLES: Record<HubSection, readonly string[]> = {
  home: OPERATIONS_ROLES,
  products: OPERATIONS_ROLES,
  studio: OPERATIONS_ROLES,
  website: OPERATIONS_ROLES,
  workflow: OPERATIONS_ROLES,
  marketing: MARKETING_VIEW_ROLES,
  finance: OPERATIONS_ROLES,
};

const any = (roles: readonly string[] | null | undefined, allowed: readonly string[]) =>
  !!roles && roles.some((r) => allowed.includes(r));

export function canSeeSection(roles: readonly string[] | null | undefined, section: HubSection): boolean {
  return any(roles, SECTION_ROLES[section]);
}

export function canEditMarketing(roles: readonly string[] | null | undefined): boolean {
  return any(roles, MARKETING_EDIT_ROLES);
}

/** Where a user lands: Home if they may see it, else their first section. */
export function homeFor(roles: readonly string[] | null | undefined): string {
  if (canSeeSection(roles, "home")) return "/";
  if (canSeeSection(roles, "marketing")) return "/marketing";
  return "/login";
}

/** Marketing role and no operations role: Flow lets them read clients and
 *  opportunity lists, not order / quote / cost pages (S1 default-deny). Hub
 *  Marketing links such a viewer to the Flow CLIENT page only. */
export function isMarketingOnly(roles: readonly string[] | null | undefined): boolean {
  return !!roles && roles.includes("marketing") && !roles.some((r) => (OPERATIONS_ROLES as readonly string[]).includes(r));
}
